import { ModuleEnum } from '../enums/ModuleEnum';
import { DatabaseClientInterface } from '../sql/DatabaseClientInterface';
import { AttributesFilterInputInterface } from '../interfaces/AttributesFilterInputInterface';
import { buildWhereFromFilters } from '../sql/buildWhereFromFilters';
import { buildWhereByModule } from '../sql/buildWhereByModule';
import { LocalesEnum } from '../enums/LocalesEnum';
import { AttributeValueWithCountInterface } from '../interfaces/AttributeValueWithCountInterface';
import { RankedProductsWithCountInterface } from '../interfaces/RankedProductsWithCountInterface';
import { RankedProductInterface } from '../interfaces/RankedProductInterface';
import { RulesetEnum } from '../enums/RulesetEnum';
import { AttributeUniqValuesInterface } from '../interfaces/AttributeUniqValuesInterface';
import { extractJsonFieldSql } from '../sql/extractJsonFieldSql';
import { ATTRIBUTES } from '../constants';
import { ProductInterface } from '../interfaces/ProductInterface';
import { uniq } from './FindByDesignResolver';
import { ComboProductsWithCountInterface } from '../interfaces/ComboProductsWithCountInterface';
import { AttributesClassInterface, ScalarProductAttributes } from '../interfaces/AttributesClassInterface';
import { ValveAccessoriesInterface } from '../interfaces/ValveAccessoriesInterface';
import { ValveAccessoriesWithTypeInterface } from '../interfaces/ValveAccessoriesWithTypeInterface';
import { guessRulesets } from '../tools/guessRuleset';
import { RegionsEnum } from '../enums/RegionsEnum';
import { getValueFromFilter } from '../tools/getValueFromFilter';
import { OrderByInterface } from '../interfaces/OrderByInterface';
import { CalcKindEnum } from '../enums/CalcKindEnum';
import { UserInputKindInterface } from '../interfaces/UserInputKindInterface';
import { check6Way, checkOCCO, checkPd, isSteam } from '../tools/tools';
import { AttributesClassWithCountInterface } from '../interfaces/AttributesClassWithCountInterface';
import { buildNumericOrder, buildStringOrder } from '../sql/buildOrderSql';
import { buildUniqueValuesSql } from '../sql/buildUniqueValuesSql';
import { prepareValuesMapping } from '../sql/prepareValuesMapping';
import { AttributesClassComboInterface } from '../interfaces/AttributesClassComboInterface';
import { mapRulesetToQuery } from '../tools/mapRulesetToQuery';

export class VssResolver {
    constructor(
        private readonly db: DatabaseClientInterface,
        private readonly mapToRankedProduct: (row: any) => RankedProductInterface,
        private readonly mapCount: (arg: any) => number,
        private readonly mapValues: (locale: LocalesEnum, rawName: LocalesEnum) => (arg: any) => AttributeValueWithCountInterface,
        private readonly mapToProduct: (row: any) => ProductInterface,
        private readonly mapToProductCombo: (row: any) => AttributesClassComboInterface,
        protected readonly preferredAttr: string,
        protected readonly boostAttr: string,
        protected readonly torqueAttr: string,
        protected readonly discontinuedAttr: string,
        private readonly region: RegionsEnum,
    ) {
    }

    public async listCount(
        module: ModuleEnum,
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
    ): Promise<number> {
        const [where, params] = buildWhereFromFilters(filter, [], rawName);
        const [moduleWhere, paramsModule] = buildWhereByModule(module, rawName, this.discontinuedAttr);
        const count = await this.db.getOne(
            `SELECT count(*) as count
             FROM product
             WHERE ${moduleWhere} ${where.length > 0 ? ` AND ${where}` : ''}`,
            [...paramsModule, ...params],
            this.mapCount
        );
        return count ? count : 0;
    }

    public async actuatorsList(
        module: ModuleEnum,
        offset: number,
        limit: number,
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
        orderByNumber: OrderByInterface[] = [],
        orderByString: OrderByInterface[] = [],
    ): Promise<AttributesClassWithCountInterface> {
        const defaultOrder = `
            cast(${extractJsonFieldSql(
            'attributes',
            `$.${this.boostAttr}.${rawName}[0]`,
            extractJsonFieldSql(
                'attributes',
                `$.${this.boostAttr}.${rawName}`,
                '0',
            ),
        )} as float) DESC, cast(${extractJsonFieldSql(
            'attributes',
            `$.${this.torqueAttr}.${rawName}[0]`,
            extractJsonFieldSql(
                'attributes',
                `$.${this.torqueAttr}.${rawName}`,
                '0',
            ),
        )} as float), label
        `;
        return this.list(
            module,
            offset,
            limit,
            filter,
            rawName,
            orderByNumber,
            orderByString,
            defaultOrder,
        );
    }

    public async list(
        module: ModuleEnum,
        offset: number,
        limit: number,
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
        orderByNumber: OrderByInterface[] = [],
        orderByString: OrderByInterface[] = [],
        defaultOrder: string = 'label',
    ): Promise<AttributesClassWithCountInterface> {
        const [where, params] = buildWhereFromFilters(filter, [], rawName);
        const [moduleWhere, paramsModule] = buildWhereByModule(module, rawName, this.discontinuedAttr);
        const count = await this.listCount(module, filter, rawName);

        const orderNumber = buildNumericOrder(orderByNumber, rawName);
        const orderString = buildStringOrder(orderByString, rawName);

        const results = await this.db.getMany(
            `SELECT id, label, attributes
             FROM product
             WHERE ${moduleWhere} ${where.length > 0 ? ` AND ${where}` : ''}
             ORDER BY ${[...orderNumber, ...orderString, defaultOrder].join(', ')}
             LIMIT ${limit} OFFSET ${offset ?? 0}`,
            [...paramsModule, ...params],
            this.mapToProduct
        );
        return {
            count,
            results: results.map((product) => product.getAttributes()),
        };
    }

    protected async getAccessories(
        label: string,
        rawName: LocalesEnum,
        accessoriesAttrs: Array<keyof Omit<AttributesClassInterface, keyof ScalarProductAttributes>>,
        filter: AttributesFilterInputInterface,
    ): Promise<[AttributesClassInterface, ValveAccessoriesWithTypeInterface[]]> {
        const product = await this.db.getOne(
            'SELECT id, label, attributes FROM product WHERE label = ?',
            [label],
            this.mapToProduct
        );
        if (!product) {
            throw new Error(`product not found - "${label}"`);
        }
        const attributes = product.getAttributes();

        const [where, params] = buildWhereFromFilters(filter, [], rawName);
        const accessoriesResolved =  await Promise.all(accessoriesAttrs.map(async (attr) => {
            const accessoriesLabels = attributes?.[attr]?.[rawName] ?? [];
            if (accessoriesLabels.length === 0) {
                return [];
            }
            const accessories = await this.db.getMany(
                `SELECT id, label, attributes
                        FROM product
                        WHERE label in (${accessoriesLabels.map(_ => '?').join(',')})
                        ${where.length > 0 ? ` AND ${where}` : ''}
                     ORDER BY ${extractJsonFieldSql('attributes', `$.8543.${rawName}`)} DESC, label 
                        `,
                [...accessoriesLabels, ...params],
                this.mapToProduct
            );

            return accessories.map<ValveAccessoriesWithTypeInterface>((item) => {
                return {
                    source: attr,
                    item: item.getAttributes(),
                }
            });
        }))

        const accessories = accessoriesResolved.reduce((acc, curr) => {
            return [...acc, ...curr];
        }, [] as ValveAccessoriesWithTypeInterface[]);

        return [
            attributes,
            accessories,
        ];
    }


    public async vssAccessories(
        rawName: LocalesEnum,
        filter: AttributesFilterInputInterface,
        labelValve: string,
        labelActuator?: string,
    ): Promise<ValveAccessoriesInterface> {
        const [valve, valveAccessories] = await this.getAccessories(
            labelValve,
            rawName,
            [
                '_581',
                '_582',
                '_3442',
                '_6311',
                '_6976'
            ],
            filter,
        );

        const valvePart = {
            valve,
            valveAccessories,
        };

        if (labelActuator) {

            const [actuator, actuatorAccessories] = await this.getAccessories(
                labelActuator,
                rawName,
                [
                    '_581',
                    '_582',
                    '_3442',
                    '_6311',
                    '_6976'
                ],
                filter,
            );

            return {
                ...valvePart,
                actuator: actuator,
                actuatorAccessories,
            };

        } else {
            return {
                ...valvePart,
                actuator: null,
                actuatorAccessories: []
            }
        }
    }


    public async vssCombo(
        rawName: LocalesEnum,
        labelValve: string,
        labelActuator: string,
    ): Promise<AttributesClassInterface[]> {
        return this.db.getMany(
            `SELECT id, label, attributes FROM product 
                 WHERE
                     lower(${extractJsonFieldSql('attributes', `$.${ATTRIBUTES.PRODUCT_TYPE_ATTR}.${rawName}`)}) IN (?)
                     AND label in (?,?)`,
            [
                JSON.stringify(['combo']),
                `${labelValve}+${labelActuator}`,
                `${labelValve}/${labelActuator}`,
            ],
            (record) => this.mapToProduct(record).getAttributes(),
        );
    }

    public async vssActuators(
        offset: number,
        limit: number,
        label: string,
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
        orderByNumber: OrderByInterface[] = [],
        orderByString: OrderByInterface[] = [],
    ): Promise<ComboProductsWithCountInterface> {
        const valve = await this.db.getOne(
            'SELECT id, label, attributes FROM product WHERE label = ?',
            [label],
            this.mapToProduct
        );
        if (!valve) {
            throw new Error('valve not found');
        }
        const attributes = valve.getAttributes();
        const type = attributes?._6691?.[rawName] ?? [];
        const isVA = type.map((item) => item.toLowerCase()).includes('Valve-actuator unit'.toLowerCase());

        if (isVA) {
            return {
                valve: attributes,
                count: 1,
                products: [{
                    type: 0,
                    product: attributes,
                }]
            }
        }

        const [where, params] = this.buildWhereForActuators(label, rawName);
        const [filterWhere, filterParams] = buildWhereFromFilters(filter, [], rawName);

        const actuatorType = extractJsonFieldSql('combo.attributes', `$.${this.preferredAttr}.${rawName}`);
        const mapType = `CASE WHEN type = 1 THEN 2 WHEN type > 1 THEN 0 ELSE 1 END DESC`;
        const orderNumber = buildNumericOrder(orderByNumber, rawName);
        const orderString = buildStringOrder(orderByString, rawName);
        const baseOrder = orderNumber.length === 0 && orderString.length === 0 ?
            `${mapType}, label`
            : 'label';

        const sql = `SELECT
            d.id as id, d.label as label, d.attributes as attributes,
            max(CASE
                WHEN lower(${actuatorType}) like '%preferred%' THEN 1
                WHEN lower(${actuatorType}) like '%manual%' THEN 2
                ELSE 0 
                END) as type
        FROM product combo
        JOIN product d on d.label =  SUBSTRING(
                combo.label,
                instr(combo.label, CASE WHEN combo.label like '%+%' THEN '+' ELSE '/' END) + 1
        )
        WHERE ${where} ${filterWhere.length > 0 ? ` AND ${filterWhere.replace(/attributes/g, 'd.attributes')}` : ''}
        GROUP BY d.label
        ORDER BY ${[...orderNumber, ...orderString, baseOrder].join(', ').replace(/attributes/g, 'd.attributes')}
        `;

        const products = await this.db.getMany(
            `${sql} LIMIT ${limit} OFFSET ${offset ?? 0}`,
            [...params, ...filterParams],
            this.mapToProductCombo
        );
        const count = await this.db.getOne(
            `WITH d as (${sql}) SELECT count(*) as count FROM d`,
            [...params, ...filterParams],
            this.mapCount
        );

        return {
            valve: attributes,
            products,
            count: count ?? 0,
        }
    }

    protected buildWhereForActuators(
        label: string,
        rawName: LocalesEnum,
        alias = 'combo'
    ) {
        const sql = `
        lower(${extractJsonFieldSql(`${alias}.attributes`, `$.${ATTRIBUTES.PRODUCT_TYPE_ATTR}.${rawName}`)}) IN (?)
        AND (${alias}.label like ? OR ${alias}.label like ?)
        `
        return [
            sql,
            [
                JSON.stringify(['combo']),
                `${label}/%`,
                `${label}+%`,
            ]
        ];
    }

    public async vssActuatorsValues(
        id: string,
        label: string,
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
        locale: LocalesEnum,
    ): Promise<AttributeUniqValuesInterface> {
        const [where, params] = this.buildWhereForActuators(label, rawName, 'product');

        const results = await this.db.getMany(`
                    SELECT id, label, attributes
                    FROM product
                    WHERE ${where}
                    ORDER BY label
            `,

            [...params],
            this.mapToProduct,
        );
        const pattern = new RegExp(`${label}[\/+]{1}`)
        const labels = results
        .map((product) => product.label.replace(pattern, ''))
        .filter(uniq);


        const { attrValue, localized, exclude} = prepareValuesMapping(
            id, rawName, [ATTRIBUTES.MEDIA]
        );

        const [whereFilter, whereParams] = buildWhereFromFilters(filter, exclude, rawName);
        const [whereModule, moduleParams] = buildWhereByModule(ModuleEnum.ActuatorSizer, rawName, this.discontinuedAttr);

        const query = buildUniqueValuesSql(
            localized,
            attrValue,
            'product',
            `label IN (${labels.map(() => '?').join(',')})
            AND ${whereModule}
            ${whereFilter.length > 0 ? ` AND ${whereFilter}` : ''}
            `
        );

        const values = await this.db.getMany(
            query,
            [...labels, ...moduleParams, ...whereParams],
            this.mapValues(locale, rawName)
        );
        return { values };
    }

    public async vssValues(
        id: string,
        volumetricFlow: string,
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
        locale: LocalesEnum,
        volumetricFlow2: string = '',
        kvs: string = '',
        kvs2: string = '',
        minDN: string = '',
        labelId: string = '',
    ): Promise<AttributeUniqValuesInterface> {

        const rulesets = await this.detectRulesets(filter, rawName, volumetricFlow, kvs);

        const {
            exclude,
            localized,
            attrValue
        } = prepareValuesMapping(id, rawName, [ATTRIBUTES.MEDIA]);


        const [finalQuery, finalParams] = this.composeFinalQuery(
            filter,
            rawName,
            rulesets,
            volumetricFlow,
            volumetricFlow2,
            kvs,
            kvs2,
            minDN,
            exclude,
        );

        const query = buildUniqueValuesSql(
            localized,
            attrValue,
            `(${finalQuery})`,
            ''
        );
        const values = await this.db.getMany(
            labelId && labelId.length > 0 ? query.replace(
                `${extractJsonFieldSql('attributes', `$.${id}`)} as label`,
                `${extractJsonFieldSql('attributes', `$.${labelId}`, extractJsonFieldSql('attributes', `$.${id}`))} as label`,
                ) : query,
            [...finalParams],
            this.mapValues(locale, rawName)
        );
        return { values };
    }

    public async vssCount(
        volumetricFlow: string,
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
        volumetricFlow2: string = '',
        kvs: string = '',
        kvs2: string = '',
        minDN: string = '',
    ): Promise<number> {
        const rulesets = await this.detectRulesets(filter, rawName, volumetricFlow, kvs);

        const [finalQuery, finalParams] = this.composeFinalQuery(
            filter,
            rawName,
            rulesets,
            volumetricFlow,
            volumetricFlow2,
            kvs,
            kvs2,
            minDN,
        );

        const count = await this.db.getOne(
            `SELECT count(id) as count FROM (${finalQuery}) as total`,
            finalParams,
            this.mapCount
        );

        return count ? count : 0;
    }

    private async innerValues(
        id :string,
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
    ): Promise<string[]> {
        const [whereModule, paramsModule] = buildWhereByModule(ModuleEnum.ValveSizer, rawName, this.discontinuedAttr);
        const [whereFilter, paramsFilter] = buildWhereFromFilters(filter, [], rawName);
        const attrValue = extractJsonFieldSql('attributes', `$.${id}.${rawName}`);
        const localized = extractJsonFieldSql('attributes', `$.${id}`);
        const query = `
            SELECT attrs.value as value, attrs.key as key, ${localized} as label, count(*) as count
            FROM product as product
            CROSS JOIN json_each(${attrValue}) as attrs
            WHERE ${whereModule}
            ${whereFilter.length > 0 ? ` AND ${whereFilter}` : ''}
            GROUP BY attrs.value
            ORDER BY attrs.value ASC;`;

        const results = await this.db.getMany(
            query,
            [...paramsModule, ...paramsFilter],
            this.mapValues(rawName, rawName)
        );
        return results.map(({value}) => value);
    }

    protected async getParams(
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
    ) {
        let types: string[] = [];
        types = getValueFromFilter(filter, '6667');
        if (types.length === 0) {
            types = await this.innerValues('6667', filter, rawName);
        }

        let patterns: string[] = [];
        patterns = getValueFromFilter(filter, '6560');
        if (patterns.length === 0) {
            patterns = await this.innerValues(
                '6560',
                filter,
                rawName,
            );
        }

        let operationModes: string[] = [];
        operationModes = getValueFromFilter(filter, '8165');
        if (operationModes.length === 0) {
            operationModes = await this.innerValues(
                '8165',
                filter,
                rawName,
            );
        }

        return {
            types,
            patterns,
            operationModes,
        }
    }

    public async detectRulesets(
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
        volumetricFlow: string,
        kvs: string,
    ): Promise<RulesetEnum[]> {
        const isFlowProvided = (volumetricFlow && volumetricFlow.length > 0) || (kvs && kvs.length > 0);
        if (!isFlowProvided) {
            return [];
        }

        const {
            types,
            patterns,
            operationModes
        } = await this.getParams(filter, rawName);

        try {
            return guessRulesets(
                this.region,
                types,
                patterns,
                operationModes,
            );
        } catch (e) {
            return [];
        }
    }

    public async vssList(
        offset: number,
        limit: number,
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
        volumetricFlow: string,
        volumetricFlow2: string = '',
        kvs: string = '',
        kvs2: string = '',
        minDN: string = '',
        orderByNumber: OrderByInterface[] = [],
        orderByString: OrderByInterface[] = [],
    ): Promise<RankedProductsWithCountInterface> {

        const rulesets = await this.detectRulesets(filter, rawName, volumetricFlow, kvs);

        const [finalQuery, finalParams] = this.composeFinalQuery(
            filter,
            rawName,
            rulesets,
            volumetricFlow,
            volumetricFlow2,
            kvs,
            kvs2,
            minDN,
        )

        const count = await this.db.getOne(
            `SELECT count(id) as count FROM (${finalQuery}) as total`,
            finalParams,
            this.mapCount
        );

        const orderNumber = buildNumericOrder(orderByNumber, rawName);
        const orderString = buildStringOrder(orderByString, rawName);

        const OrderBySql = rulesets.length === 0 ||
            rulesets.length > 1 ||
            [...orderByNumber, ...orderByString].length > 0 ?
                ` ORDER BY ${[ ...orderNumber, ...orderString, 'rank DESC', 'label'].join(', ')} `
            : '';

        const results = await this.db.getMany(
            `${finalQuery}
             ${OrderBySql}
             LIMIT ${limit} OFFSET ${offset ?? 0}`,
            finalParams,
            this.mapToRankedProduct
        );

        return {
            __typename: "RankedProductsWithCount",
            count: count ? count : 0,
            products: results.map(
                ({ product, rank }) => ({ product: product.getAttributes(), rank }),
            ),
        };
    }

    protected composeFinalQuery(
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
        rulesets: RulesetEnum[],
        volumetricFlow: string = '',
        volumetricFlow2: string = '',
        kvs: string = '',
        kvs2: string = '',
        minDN: string = '',
        exclude: string[] = []
    ): [string, string[]] {
        const [where, params] = this.getWhere(filter, rawName, exclude);

        const finalQueryParts: string[] = [];
        const finalQueryParams: string[] = [];

        for (const ruleset of rulesets) {
            const { buildQuery, selectFields, order } = mapRulesetToQuery(ruleset);
            const [rulesetQuery, rulesetParams] = buildQuery({
                selectFields,
                rawName,
                filter: where,
                discontinuedAttr: this.discontinuedAttr,
                volumetricFlow,
                volumetricFlow2,
                kvs,
                kvs2,
                minDN,
            });
            finalQueryParts.push(`
            SELECT rank, id, label, attributes FROM (
                ${rulesetQuery}
                ORDER BY ${order}
              ) as ${ruleset}
            `);
            [...params, ...rulesetParams].forEach((p) => finalQueryParams.push(p));
        }

        if (finalQueryParts.length === 0) {
            finalQueryParts.push(`
                SELECT 3 as rank, id, label, attributes FROM product as product
                WHERE ${where}
            `);
            params.forEach((p) => finalQueryParams.push(p));
        }
        return [
            `SELECT * FROM (${finalQueryParts.join(' UNION ')})`,
            finalQueryParams,
        ]
    }

    protected getWhere(
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
        exclude: string[] = [],
    ): [string, string[]] {
        const [where, params] = buildWhereFromFilters(filter, exclude, rawName);
        const [moduleWhere, paramsModule] = buildWhereByModule(ModuleEnum.ValveSizer, rawName, this.discontinuedAttr);
        return [
            `${moduleWhere} ${where.length > 0 ? ` AND ${where}` : ''}`,
            [...paramsModule, ...params],
        ];
    }

    public async userInputKind(
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
    ): Promise<UserInputKindInterface> {

        const {
           operationModes,
        } = await this.getParams(filter, rawName);

        let fluids: string[] = [];
        fluids = getValueFromFilter(filter, '6673');
        if (fluids.length === 0) {
            fluids = await this.innerValues('6673', filter, rawName);
        }

        const types = await this.innerValues('6667', filter, rawName);

        const isNotOCCO = checkOCCO(operationModes);

        const is6WayRequired = check6Way(types);

        if (isSteam(fluids)) {
            return {
                kind: CalcKindEnum.Steam,
                is6WayRequired,
                isNotOCCO,
            };
        }

        if (checkPd(types)) {
            if (rawName === LocalesEnum.en_US) {
                return {
                    kind: CalcKindEnum.Kvs,
                    is6WayRequired,
                    isNotOCCO,
                };
            }
            if (is6WayRequired) {
                return  {
                    kind: CalcKindEnum.Kvs,
                    is6WayRequired,
                    isNotOCCO,
                };
            }
            if (isNotOCCO) {
                return  {
                    kind: CalcKindEnum.Kvs,
                    is6WayRequired,
                    isNotOCCO,
                };
            }
        }

        return {
            kind: CalcKindEnum.Flow,
            is6WayRequired,
            isNotOCCO,
        };
    }

}
